import type {
  AssignmentCommandBody,
  AssignmentPreviewDto,
  ChangeVehicleAssignmentBody,
  CompletionApplyBody,
  CompletionPreviewBody,
  CompletionPreviewDto,
  DecideVehicleEarlyEndBody,
  EarlyEndApprovalPreviewDto,
  PeriodApplyBody,
  PeriodCommand,
  PeriodPreviewDto,
  RepairBody,
  RepairPreviewDto,
  RepairResultDto,
  RequestAssignmentHistoryDto,
  RequestVehicleEarlyEndInput,
  RequestVehicleEarlyEndPreviewInput,
  VehicleRequestDto,
} from '@technic/contracts';
import { apiFetch } from '@shared/api';
import type {
  AssignmentCommandResultDto,
  VehicleRequestCompletionResultDto,
  VehicleRequestPeriodResultDto,
} from './commandResults';

/**
 * Двери заказа: закрытие фактической датой, смена техники, правка срока, команда машиниста, ремонт
 * истории назначения и досрочное завершение.
 *
 * Дверь — не просто ручка, которая пишет. Её команда двигает не поле, а сцепку: срок работ, бумагу
 * строгой отчётности, решения истории о технике и человеке, часы смен и дни, стоящие в рейсах.
 * Отсюда общий порядок обращения, одинаковый у всех команд этого модуля:
 *
 *   — у двери есть пара «предпросмотр → команда», и обе половины считают по одним и тем же входам.
 *     Где входы совпадают целиком, тело у пары буквально одно (`ChangeVehicleAssignmentBody`,
 *     `AssignmentCommandBody`, `RepairBody`); где нет — тело предпросмотра является семантической
 *     половиной боевого (Л1), из которой убрано ровно то, что он сам и вычисляет. Своя схема у
 *     предпросмотра разошлась бы с боевой на первом же новом поле, и окно начало бы обещать не то,
 *     что исполнит команда;
 *   — предпросмотр ничего не пишет и отпечатка не принимает никогда: он его и вычисляет;
 *   — подтверждения (отпечаток показанного, перечень гасимых решений, снимаемые часы,
 *     переоформляемые листы, ключ операции) команда везёт ровно те, которые ей выдал предпросмотр.
 *     Присутствие каждого задаёт ОТВЕТ СЕРВЕРА, а не желание клиента: лишнее подтверждение
 *     отвергается так же строго, как недостающее, и какой ветвью пойдёт команда, решает сервер;
 *   — повтор по тому же ключу операции отвечает тем же: ключ делает команду безопасной для второго
 *     нажатия и для сети, потерявшей ответ. Чем именно отвечают двери — в `commandResults`.
 *
 * Объяснено это здесь один раз, а не у каждой ручки: правило у них общее, а повторённое семнадцать
 * раз оно разошлось бы на первой же правке. У ручек ниже остаётся только их собственное — что
 * считает их предпросмотр, какой ветви подтверждения нужны и чем их случай отличается.
 *
 * Схемы контрактов при этом мягче описанного, и это фазирование, а не послабление (Ж5, И5):
 * отпечаток объявлен необязательным, потому что старые вкладки живут дольше выката, а спрашивает
 * его сервер по режиму чтения. Сверять обещанное схемой — значит уронить дверь у всех в момент
 * деплоя.
 *
 * Три ручки пары не имеют, и это не исключение из правила, а его край. `assignmentHistory` и
 * `repairState` ничего не пишут: это первый вопрос тех самых окон, и ответ на него решает, какая
 * дверь вообще открыта. `cancelEarlyEnd` отзывает запрос, который ещё ничего не применил, — там
 * нечего показывать и нечего подтверждать.
 */
export const vehicleRequestDoors = {
  /**
   * Последствия закрытия заказа техники фактической датой до его совершения (ADR 0178, Р1, Р22):
   * какие листы сгорят и выпишутся, какие решения о технике погаснут, какие часы смен исчезнут и
   * какие дни уйдут из рейсов. Ничего не пишет.
   *
   * Тело — семантическая половина боевого (Л1 плана периодов): факт целиком, включая фактическую
   * дату, — без неё сокращать нечего, и предпросмотр показал бы план несуществующей команды.
   * Отпечатков он не принимает вовсе: он их и вычисляет, а присланный отпечаток означает ошибку
   * клиента и кончается 400.
   *
   * Арендодатель сюда не ходит (Р16): его ветвь ни срока, ни бумаги не трогает, план у неё пуст по
   * построению, и подтверждать ему нечего.
   */
  completionPreview: (id: string, body: CompletionPreviewBody) =>
    apiFetch<CompletionPreviewDto>(`/vehicle-requests/${id}/completion/preview`, {
      method: 'POST',
      body,
    }),
  /**
   * Закрыть заказ техники на объект фактической датой — своя дверь, а не «Выполнена» у статусной
   * ручки (Р1, ADR 0178). Статусная ручка это закрытие с того же выпуска отвергает.
   *
   * Почему дверь: закрытие фактом сокращает срок, переписывает бумагу, гасит решения истории и
   * стирает часы смен за фактической датой — то есть команда с предпросмотром, отпечатками,
   * условной авторизацией и, у закрытия задним числом, записью в журнал коррекций. Всё это уже
   * написано каноном команд истории, и вторая его копия в статусной ручке разошлась бы с первой.
   *
   * Что уезжает подтверждениями (Р28): `previewFingerprint` — последствия, которые человек видел;
   * `cancelGroupsFingerprint` — перечень гасимых решений о технике; `clearedShiftsFingerprint` —
   * снимаемые часы; `unlockFingerprint` — отработанные листы, которые переоформит операция;
   * `operation` — причина и ключ идемпотентности там, где нужна запись журнала. Присутствие
   * каждого задаёт **ответ сервера**, а не желание клиента: лишнее подтверждение отвергается так
   * же строго, как недостающее.
   */
  complete: (id: string, body: CompletionApplyBody) =>
    apiFetch<VehicleRequestCompletionResultDto>(`/vehicle-requests/${id}/completion`, {
      method: 'POST',
      body,
    }),
  /**
   * Сменить машину и ставки у заявки, которая уже в работе (ADR 0048): техника сломалась, ушла на
   * другой объект или её перепутали при переводе в работу. Статус при этом не меняется — тем и
   * отличается от повторного перевода в работу, которым назначение переписывали до сих пор.
   * Заявка переезжает в рейс новой машины той же транзакцией.
   */
  changeAssignment: (id: string, body: ChangeVehicleAssignmentBody) =>
    apiFetch<VehicleRequestDto>(`/vehicle-requests/${id}/assignment`, { method: 'PATCH', body }),
  /**
   * Последствия смены техники до её совершения (волна 4a плана `docs/assignment-periods-plan.md`,
   * §7): какие номера ЭСМ-2 сгорят и какие выпишутся, какие подписи объекта слетят, каких дней не
   * хватает машинисту. Ничего не пишет.
   *
   * Тело — то же самое, что у боевой ручки, и это существенно: план считается по машине, машинисту
   * и коррекционному блоку из окна, а своя схема разошлась бы с боевой на первом же новом поле — и
   * вместе с ней разошёлся бы отпечаток, которым сервер сверяет обещанное.
   *
   * Отпечаток сюда не передаётся никогда: предпросмотр его выдаёт, а не спрашивает.
   */
  assignmentPreview: (id: string, body: ChangeVehicleAssignmentBody) =>
    apiFetch<AssignmentPreviewDto>(`/vehicle-requests/${id}/assignment/preview`, {
      method: 'POST',
      body,
    }),
  /**
   * Последствия правки срока до её совершения (волна 4a плана `docs/assignment-periods-plan.md`,
   * Ж4, З5, Л1): какие бланки ЭСМ-2 сгорят и какие выпишутся, какие решения о технике погасит
   * сокращение (Д2), какие отработанные листы придётся переоформить и нужна ли причина. Ничего
   * не пишет.
   *
   * Тело у предпросмотра — семантическая половина боевого (Л1): отпечатка, исхода и ключей листов
   * он ещё не знает — он их и вычисляет. Отпечаток сюда не передаётся никогда.
   */
  periodPreview: (id: string, body: PeriodCommand) =>
    apiFetch<PeriodPreviewDto>(`/vehicle-requests/${id}/period/preview`, { method: 'POST', body }),
  /**
   * Правка срока работ — своя дверь, а не поля широкого `PATCH /:id` (Ж4, З5).
   *
   * Узкое тело «продлить до 31 августа» в широкий маршрут не легло бы: там строгий union с
   * обязательными типом заявки, техникой, заказчиком, контактами и файлами. Но главное не форма
   * тела, а рукопожатия: сокращение способно **погасить решения о технике** за новым концом срока
   * (Д2), и портал обязан показать перечень и вернуть подтверждение — иначе сервер отвечает 422.
   *
   * Что уезжает подтверждениями: `previewFingerprint` — последствия, которые человек видел;
   * `cancelGroupsFingerprint` — перечень гасимых решений; `unlockFingerprint` — отработанные
   * листы, которые переоформит операция (присутствие поля задаёт исход, а не желание клиента);
   * `operation` — причина и ключ идемпотентности там, где нужна запись в журнале коррекций.
   *
   * Широкий маршрут при этом продолжает принимать даты (И5): выкат не одномоментный, и старый
   * путь остаётся рабочим до cutover.
   */
  changePeriod: (id: string, body: PeriodApplyBody) =>
    apiFetch<VehicleRequestPeriodResultDto>(`/vehicle-requests/${id}/period`, {
      method: 'PATCH',
      body,
    }),
  /**
   * История назначения заявки — то, из чего портал строит «Состав по датам» (этап 6 плана
   * `docs/assignment-periods-plan.md`, §9): с какого числа какая машина и какой машинист.
   *
   * Погашенные строки приходят наравне с актуальными: журнал заявки читают, чтобы понять, **что**
   * правили, а не только чем дело кончилось. Состояние готовности отдаётся той же ручкой (Р26) —
   * окно обязано отличить «истории нет» от «история есть, но неполна», и второй запрос за этим
   * означал бы, что между ними состояние успевает измениться.
   */
  assignmentHistory: (id: string) =>
    apiFetch<RequestAssignmentHistoryDto>(`/vehicle-requests/${id}/assignment-changes`),
  /**
   * Последствия команды машиниста до её совершения (§8). Ничего не пишет.
   *
   * Двухфазный (Р16): первый вызов идёт без якорей и возвращает `requiredAnchors` — границы, на
   * которых свёртка осталась бы без человека; второй, с названными именами, отдаёт окончательный
   * план и отпечаток, который и принимает боевая ручка. Одной фазы не хватает: пока люди не
   * названы, набор последствий ещё неизвестен, и подтверждать нечего.
   *
   * Тело — то же самое, что у боевой ручки (§8): расчёт обязан идти по тем входам, по которым его
   * потом исполнит команда, иначе окно начнёт обещать не то. Отпечаток сюда не передаётся никогда:
   * предпросмотр его выдаёт, а не спрашивает.
   */
  assignmentChangePreview: (id: string, body: AssignmentCommandBody) =>
    apiFetch<AssignmentPreviewDto>(`/vehicle-requests/${id}/assignment-changes/preview`, {
      method: 'POST',
      body,
    }),
  /**
   * Команда машиниста: `set` — назначить человека с даты, `cancel` — снять запланированное
   * решение (Р13).
   *
   * Ответ — состояние заявки после команды, а не отчёт о ней (Р9): версия, переписанные номера
   * ЭСМ-2 и вся история заново. Повтор по тому же ключу операции отвечает то же самое — по нему и
   * видно, что работы не было (`repeated`).
   */
  changeAssignmentMachinist: (id: string, body: AssignmentCommandBody) =>
    apiFetch<AssignmentCommandResultDto>(`/vehicle-requests/${id}/assignment-changes`, {
      method: 'POST',
      body,
    }),
  /**
   * Осмотр истории: что в ней чинить (подэтап 6a плана `docs/assignment-periods-plan.md`, Р29).
   *
   * Первый запрос окна «Починка истории». Своя ручка, а не предпросмотр с пустым телом: тело
   * предпросмотра нарочно одно с боевым, а спросить «что чинить» окно обязано **до** того, как
   * назовёт работу. Главное в ответе — `fillableGaps`: какие `unknown`-промежутки заблокированы и
   * потому адресуются заполнением, а какие правятся якорями, знает только сервер — это зависит от
   * отменяемости бумаги. Ничего не пишет.
   */
  repairState: (id: string) =>
    apiFetch<RepairPreviewDto>(`/vehicle-requests/${id}/assignment-changes/repair/state`),
  /**
   * Последствия ремонта до его совершения (Р29): какие бланки сгорят, какие выпишутся задним
   * числом, какие отработанные листы придётся переоформить и станет ли история полной.
   *
   * Тело — то же самое, что у боевой ручки (§8). Отпечаток сюда не передаётся никогда:
   * предпросмотр его выдаёт, а не спрашивает.
   */
  repairPreview: (id: string, body: RepairBody) =>
    apiFetch<RepairPreviewDto>(`/vehicle-requests/${id}/assignment-changes/repair/preview`, {
      method: 'POST',
      body,
    }),
  /**
   * Ремонт истории: якоря на пробелах машиниста, заполнение `unknown` известным человеком,
   * решение о машине после конца срока и отмена заполнения (Р29, Р31).
   *
   * Заполнение выписывает недостающие бланки **задним числом** — расход строгой отчётности
   * реальный, и окно обязано показать номера до нажатия.
   */
  repairAssignmentHistory: (id: string, body: RepairBody) =>
    apiFetch<RepairResultDto>(`/vehicle-requests/${id}/assignment-changes/repair`, {
      method: 'POST',
      body,
    }),
  /**
   * Последствия досрочного завершения глазами того, кто просит **сам за себя** (Р19, Р26): его
   * запрос сервер применяет немедленно, значит и последствия у него есть сегодня.
   *
   * Ответ **обезличен**: числа и даты, без номеров бланков и фамилий. Причина не в скупости, а в
   * правах — визирующий сокращение (руководитель строительства) прав на журнал листов не имеет
   * вовсе, и спроси дверь это право, обе применяющие ветви стали бы недоступны тем, ради кого они
   * существуют (решение заказчика по В9).
   *
   * Тому, чей запрос уйдёт на визу, сервер отвечает 422: он ничего не применяет, а обещание «что
   * будет, когда завизируют» к моменту визы устареет — его и покажет предпросмотр решения.
   */
  earlyEndPreview: (id: string, body: RequestVehicleEarlyEndPreviewInput) =>
    apiFetch<EarlyEndApprovalPreviewDto>(`/vehicle-requests/${id}/early-end/preview`, {
      method: 'POST',
      body,
    }),
  /**
   * Последствия визы — то же обезличенное тело, но по **чужому** запросу и глазами визирующего.
   *
   * Предпросмотр заявителя сюда не годится и физически не подойдёт: между обращением и решением
   * проходит время, состояние меняется, решает другой человек, а имя двери входит в отпечаток
   * (Р19). Виза получает свою пару «предпросмотр → решение», и отпечаток берётся из неё.
   */
  earlyEndDecisionPreview: (id: string, version: number) =>
    apiFetch<EarlyEndApprovalPreviewDto>(`/vehicle-requests/${id}/early-end/decision/preview`, {
      method: 'POST',
      body: { approved: true, version },
    }),
  /**
   * Досрочное завершение заказа спецтехники (ADR 0044): техника освободилась раньше срока.
   * Запрос уходит на визу руководителя строительства; его собственный сервер применяет сразу.
   *
   * Отпечатки и ключ операции необязательны и уезжают только у применяющей ветви — той, где просит
   * сам визирующий (Р19, Р28): у ждущей визы подтверждать нечего, и присланное подтверждение она
   * отвергает 422. Какая ветвь пойдёт, решает сервер по субъекту, а не портал по телу; окно шлёт
   * подтверждения ровно тогда, когда ему ответил предпросмотр.
   */
  requestEarlyEnd: (id: string, body: RequestVehicleEarlyEndInput) =>
    apiFetch<VehicleRequestDto>(`/vehicle-requests/${id}/early-end`, { method: 'POST', body }),
  /**
   * Решение по запросу: `approved: false` — отказ, и тогда причина обязательна.
   *
   * У визы тело растёт теми же тремя подтверждениями, что у применяющего запроса: она двигает срок
   * и переписывает бумагу спустя часы или дни после обращения. У отказа их нет и быть не может —
   * он не применяет ничего, и схема отвергает их 400.
   */
  decideEarlyEnd: (id: string, body: DecideVehicleEarlyEndBody) =>
    apiFetch<VehicleRequestDto>(`/vehicle-requests/${id}/early-end`, { method: 'PATCH', body }),
  /** Отозвать запрос, пока он ждёт визы: «отбой, техника нужна». */
  cancelEarlyEnd: (id: string) =>
    apiFetch<VehicleRequestDto>(`/vehicle-requests/${id}/early-end`, { method: 'DELETE' }),
};
